import {
  ExaminationModel,
  ExaminationValueType,
} from '@/pages/ScreeningPage/examinationModel'
import {
  ModifiedBleedingValues,
  ModifiedIndexBleedingModel,
} from '@/pages/ScreeningPage/modifiedIndexBleedingModel'
import { CellsIDS } from '@/pages/ScreeningPage/screeningModel'

export type ExaminationModelResponse = {
  id: CellsIDS
  furcation: boolean
  is_active: boolean
  value: ExaminationValueType
}

export type ModifiedIndexBleedingModelResponse = {
  id: CellsIDS
  values: ModifiedBleedingValues
}

type ScreeningReport = {
  date_at: string
  examination: ExaminationModelResponse[]
  modified_bleeding_index: ModifiedIndexBleedingModelResponse[]
  cpitn_value: string
  modified_bleeding_index_value: number
  re_cleaning: number
  re_cleaning_date: string
  teeth_stone: boolean
  pigmented_plaque: boolean
  plaque_index: number
  recommendations: string[]
}

export interface ScreeningControllerInterface {
  dateAt: string
  cpitnValue: string
  modifiedBleedingIndexValue: number
  plaqueIndex: number
  reCleaning: number
  pigmentedPlaque: boolean
  teethStone: boolean
  examinationCells: ExaminationModel[]
  modifiedBleedingCells: ModifiedIndexBleedingModel[]
  recommendations: string[]
  reCleaningDate: string
  setData(report: ScreeningReport): void
  isUpdatedExamination(): boolean
  isUpdatedModifiedBleedingIndex(): boolean
  getExaminationCellsForSend(): ExaminationModelResponse[]
  getModifiedIndexBleedingCellsForSend(): ModifiedIndexBleedingModelResponse[]
  setInitialValues(): void
}

export class ScreeningController implements ScreeningControllerInterface {
  public dateAt: string
  public cpitnValue: string
  public modifiedBleedingIndexValue: number
  public plaqueIndex: number
  public reCleaning: number
  public pigmentedPlaque: boolean
  public teethStone: boolean
  public examinationCells: ExaminationModel[]
  public modifiedBleedingCells: ModifiedIndexBleedingModel[]
  public recommendations: string[]
  public reCleaningDate: string
  protected initialExaminationCells: string
  protected initialModifiedBleedingCells: string

  constructor() {
    this.setInitialValues()
  }

  public setData(report: ScreeningReport): void {
    this.dateAt = report.date_at
    this.cpitnValue = report.cpitn_value
    this.modifiedBleedingIndexValue = report.modified_bleeding_index_value
    this.plaqueIndex = report.plaque_index
    this.pigmentedPlaque = report.pigmented_plaque
    this.reCleaning = report.re_cleaning
    this.reCleaningDate = report.re_cleaning_date
    this.teethStone = report.teeth_stone
    this.recommendations = report.recommendations
    this.modifiedBleedingCells = report.modified_bleeding_index.map(
      (cell) => new ModifiedIndexBleedingModel(cell)
    )
    this.examinationCells = report.examination.map(
      (cell) => new ExaminationModel(cell)
    )
    this.initialExaminationCells = JSON.stringify(this.examinationCells)
    this.initialModifiedBleedingCells = JSON.stringify(
      this.modifiedBleedingCells
    )
  }

  public isUpdatedExamination(): boolean {
    return (
      this.initialExaminationCells !== JSON.stringify(this.examinationCells)
    )
  }

  public isUpdatedModifiedBleedingIndex(): boolean {
    return (
      this.initialModifiedBleedingCells !==
      JSON.stringify(this.modifiedBleedingCells)
    )
  }

  public getExaminationCellsForSend(): ExaminationModelResponse[] {
    return this.examinationCells.map((cell: ExaminationModel) =>
      cell.getCellForSend()
    )
  }

  public getModifiedIndexBleedingCellsForSend(): ModifiedIndexBleedingModelResponse[] {
    return this.modifiedBleedingCells.map((cell: ModifiedIndexBleedingModel) =>
      cell.getCellForSend()
    )
  }

  public setInitialValues(): void {
    this.dateAt = new Date().toDateString()
    this.cpitnValue = '0'
    this.modifiedBleedingIndexValue = 0
    this.plaqueIndex = 0
    this.pigmentedPlaque = false
    this.reCleaning = 0
    this.reCleaningDate = new Date().toDateString()
    this.teethStone = false
    this.recommendations = []
    this.modifiedBleedingCells = this.getInitialModifiedIndexBleedingCells()
    this.examinationCells = this.getInitialExaminationCells()
  }

  protected getInitialModifiedIndexBleedingCells(): ModifiedIndexBleedingModel[] {
    const cells: ModifiedIndexBleedingModel[] = []
    const IDS: CellsIDS[] = [0, 1, 2, 3, 4, 5]

    IDS.forEach((id) => {
      const values = {
        up: false,
        down: false,
        right: false,
        left: false,
      }
      const cell = { id, values }
      cells.push(new ModifiedIndexBleedingModel(cell))
    })

    return cells
  }

  protected getInitialExaminationCells(): ExaminationModel[] {
    const cells: ExaminationModel[] = []
    const IDS: CellsIDS[] = [0, 1, 2, 3, 4, 5]

    IDS.forEach((id) => {
      const cell: ExaminationModelResponse = {
        id,
        is_active: true,
        furcation: false,
        value: 0,
      }
      cells.push(new ExaminationModel(cell))
    })

    return cells
  }
}
